import React from 'react';
import {
  Box, Grid, Text, Flex, List, ListItem,
  Tabs, TabList, Tab, TabPanels, TabPanel, AspectRatio,
} from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';

const MotionBox = motion(Box);

const USE_CASES = [
  {
    id: 'manufacturing',
    num: '01',
    name: 'Manufacturing',
    videoLabel: 'O-ID Manufacturing Operations',
    videoDuration: '2:34',
    description:
      'Assembly lines, quality inspection, and precision material handling. O-ID adapts its end effectors and locomotion to match any production specification. ISO-rated cleanroom modules available.',
    highlights: [
      'Sub-millimeter end-effector precision',
      'ISO-rated cleanroom module variants',
      '24/7 continuous operation cycles',
      'Collaborative human-robot workflow',
    ],
  },
  {
    id: 'logistics',
    num: '02',
    name: 'Logistics',
    videoLabel: 'O-ID Warehouse Operations',
    videoDuration: '1:58',
    description:
      'Warehouse operations, dynamic load management, and last-mile handling in any facility layout. Hot-swap power modules keep O-ID running across full shifts without interruption.',
    highlights: [
      'Up to 40 kg payload capacity',
      'Dynamic facility map adaptation',
      'Hot-swap battery, zero downtime',
      'Integrates with WMS/ERP systems',
    ],
  },
  {
    id: 'hazardous',
    num: '03',
    name: 'Hazardous',
    videoLabel: 'O-ID Hazardous Environment Deployment',
    videoDuration: '3:12',
    description:
      'Radiation zones, extreme temperatures, chemical exposure. Purpose-built module variants rated for hostile conditions. Operators stay safe, O-ID goes in first.',
    highlights: [
      'IP67 hazmat module seals',
      'Radiation-hardened compute option',
      'Thermal range: -40°C to +85°C',
      'Full-decontamination compatible',
    ],
  },
  {
    id: 'healthcare',
    num: '04',
    name: 'Healthcare',
    videoLabel: 'O-ID Clinical Support Operations',
    videoDuration: '2:07',
    description:
      'Sterile logistics, patient assistance, and supply distribution in clinical environments. Smooth, predictable motion profiles ensure safety around patients and clinical staff.',
    highlights: [
      'Sterile-rated module coatings',
      'Ultra-quiet drive systems',
      'Force-limited manipulation modes',
      'EMC-compatible for medical zones',
    ],
  },
  {
    id: 'research',
    num: '05',
    name: 'Research',
    videoLabel: 'O-ID Research & Defense Platform',
    videoDuration: '4:20',
    description:
      'Rapid hardware prototyping, autonomous field data collection, and defense applications. Open SDK enables custom module integration and full sensor stack control.',
    highlights: [
      'Open hardware expansion bus',
      'Full ROS2 / custom SDK support',
      'Mesh radio, GPS-denied ops',
      'Custom module qualification program',
    ],
  },
];

function VideoPlaceholder({ useCase }) {
  return (
    <AspectRatio ratio={16 / 9} w="100%">
      <Box bg="oid.bgBlack" position="relative">
        {/* Grid overlay */}
        <Box position="absolute" inset={0} zIndex={1} pointerEvents="none"
          sx={{
            backgroundImage: `
              repeating-linear-gradient(0deg,
                transparent, transparent calc(100%/8 - 0.5px),
                rgba(255,255,255,0.05) calc(100%/8 - 0.5px),
                rgba(255,255,255,0.05) calc(100%/8)),
              repeating-linear-gradient(90deg,
                transparent, transparent calc(100%/14 - 0.5px),
                rgba(255,255,255,0.05) calc(100%/14 - 0.5px),
                rgba(255,255,255,0.05) calc(100%/14))`,
          }} />

        {/* Placeholder label */}
        <Text position="absolute" top={4} left={5} zIndex={3}
          fontSize="9px" fontWeight={600} letterSpacing="0.35em"
          textTransform="uppercase" color="rgba(255,255,255,0.25)">
          Video Placeholder
        </Text>

        {/* Duration badge */}
        <Text position="absolute" top={4} right={5} zIndex={3}
          fontSize="11px" fontWeight={500} letterSpacing="0.08em"
          color="rgba(255,255,255,0.35)">
          0:00 / {useCase.videoDuration}
        </Text>

        {/* Play button */}
        <Box position="absolute" inset={0} zIndex={2}
          display="flex" alignItems="center" justifyContent="center"
          aria-hidden="true">
          <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
            <circle cx="36" cy="36" r="34" stroke="#ff3c23" strokeWidth="1.5"
              strokeOpacity="0.8"/>
            <polygon points="29,22 52,36 29,50" fill="#ff3c23"/>
          </svg>
        </Box>

        {/* Bottom caption bar */}
        <Box position="absolute" bottom={0} left={0} right={0} zIndex={3}
          px={5} py={4}
          bg="linear-gradient(0deg, rgba(0,0,0,0.85) 0%, transparent 100%)">
          <Text fontSize="11px" fontWeight={600} letterSpacing="0.2em"
            textTransform="uppercase" color="oid.accent" mb={1}>
            {useCase.num}. {useCase.name}
          </Text>
          <Text fontSize="xs" fontWeight={400} color="rgba(255,255,255,0.7)">
            {useCase.videoLabel}
          </Text>
        </Box>
      </Box>
    </AspectRatio>
  );
}

function UseCases() {
  const shouldReduce = useReducedMotion();
  const ease = [0.25, 0.1, 0.25, 1];

  return (
    <Box as="section" id="use-cases" aria-label="Applications and use cases"
      bg="oid.bgWhite" py={{ base: '55px', md: '89px' }}>
      <Box maxWidth="1600px" mx="auto" px={{ base: 6, lg: 12 }}>

      {/* Header */}
      <MotionBox
        initial={shouldReduce ? false : { opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.7, ease }}
        mb={12}>
        <Box mb={6}>
          <Box as="span" display="block" width="24px" height="2px"
            bg="oid.accent" mb={4} aria-hidden="true" />
          <Text fontSize="11px" fontWeight={600} letterSpacing="0.3em"
            textTransform="uppercase" color="oid.accent">06. Applications</Text>
        </Box>
        <Text as="h2" fontWeight={700} lineHeight={1.1} color="oid.text"
          sx={{ fontSize: 'clamp(28px, 3.5vw, 48px)' }}>
          One robot.{' '}
          <Text as="span" color="oid.muted" fontWeight={400}>Every environment.</Text>
        </Text>
      </MotionBox>

      {/* Tabs */}
      <MotionBox
        initial={shouldReduce ? false : { opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.7, delay: 0.12, ease }}>
        <Tabs variant="oid" defaultIndex={0} isLazy>
          <TabList>
            {USE_CASES.map(uc => (
              <Tab key={uc.id}>
                <Box>
                  <Text fontSize="9px" fontWeight={700} letterSpacing="0.25em"
                    color="oid.accent" mb="2px" aria-hidden="true">
                    {uc.num}
                  </Text>
                  {uc.name}
                </Box>
              </Tab>
            ))}
          </TabList>

          <TabPanels>
            {USE_CASES.map(uc => (
              <TabPanel key={uc.id}>
                <Grid
                  templateColumns={{ base: '1fr', lg: '61.8% 38.2%' }}
                  gap={{ base: 8, lg: 14 }}
                  alignItems="start">

                  {/* Video — 61.8% */}
                  <Box>
                    <VideoPlaceholder useCase={uc} />
                  </Box>

                  {/* Description — 38.2% */}
                  <Box>
                    <Text as="h3" fontWeight={700} color="oid.text"
                      sx={{ fontSize: 'clamp(20px, 2vw, 28px)' }}
                      lineHeight={1.2} mb={5}>
                      {uc.name}
                    </Text>
                    <Text fontSize="0.9375rem" fontWeight={400} color="oid.muted"
                      lineHeight={1.618} mb={8}>
                      {uc.description}
                    </Text>
                    <Text fontSize="11px" fontWeight={600} letterSpacing="0.25em"
                      textTransform="uppercase" color="oid.text" mb={4}>
                      Key capabilities
                    </Text>
                    <List as="ul" listStyleType="none" m={0} p={0}
                      display="flex" flexDirection="column" gap={3}>
                      {uc.highlights.map(h => (
                        <ListItem key={h} display="flex" alignItems="flex-start" gap={3}>
                          <Box mt="6px" width="5px" height="5px" borderRadius="full"
                            bg="oid.accent" flexShrink={0} aria-hidden="true" />
                          <Text fontSize="sm" fontWeight={500} color="oid.text"
                            lineHeight={1.5}>{h}</Text>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Grid>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </MotionBox>
      </Box>
    </Box>
  );
}
export default UseCases;
